if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.imagePrefetches = new Map();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        // Use event delegation so thumbnails added later (e.g. via variant change) are still clickable.
        this.elements.thumbnails.addEventListener('click', (event) => {
          const item = event.target.closest('[data-target]');
          if (!item || !this.elements.thumbnails.contains(item)) return;
          this.setActiveMedia(item.dataset.target, false);
        });
        if (this.classList.contains('pdp-gallery')) {
          this.elements.thumbnails.addEventListener('pointerover', this.handleThumbnailIntent.bind(this));
          this.elements.thumbnails.addEventListener('pointerdown', this.handleThumbnailIntent.bind(this));
          this.elements.thumbnails.addEventListener('focusin', this.handleThumbnailIntent.bind(this));
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.imagePrefetches?.forEach((state) => {
          state.status = 'cancelled';
          if (state.preloader) state.preloader.src = '';
        });
        this.imagePrefetches?.clear();
      }

      handleThumbnailIntent(event) {
        const item = event.target.closest('[data-target]');
        if (!item || !this.elements.thumbnails.contains(item)) return;
        this.prefetchMedia(item.dataset.target);
      }

      prefetchMedia(mediaId) {
        const media = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!media) return;

        const image =
          media.querySelector('.pdp-gallery-media__poster-image') ||
          media.querySelector('.pdp-gallery-media__image');
        const placeholderSrc = image?.closest('[data-pdp-placeholder-src]')?.dataset.pdpPlaceholderSrc;
        if (image && placeholderSrc) this.prefetchImage(image, placeholderSrc);
      }

      prefetchImage(image, placeholderSrc) {
        const existingState = this.imagePrefetches.get(image);
        if (existingState?.status === 'pending' || existingState?.status === 'ready') return;

        const state = existingState || {
          fullSrc: image.getAttribute('src'),
          fullSrcset: image.getAttribute('srcset'),
          fullSizes: image.getAttribute('sizes'),
          fullLoading: image.getAttribute('loading'),
          fullFetchPriority: image.getAttribute('fetchpriority'),
          placeholderSrc,
        };

        if (!state.fullSrc || (image.complete && image.naturalWidth > 0 && !image.dataset.pdpPlaceholderShown)) {
          state.status = 'ready';
          this.imagePrefetches.set(image, state);
          return;
        }

        state.status = 'pending';
        this.imagePrefetches.set(image, state);

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.setAttribute('loading', 'eager');
        image.setAttribute('fetchpriority', 'low');
        image.setAttribute('src', state.placeholderSrc);
        image.dataset.pdpPlaceholderShown = 'true';

        const preloader = new Image();
        state.preloader = preloader;
        preloader.decoding = 'async';
        preloader.setAttribute('fetchpriority', 'low');

        const showFullImage = () => {
          if (state.status !== 'pending') return;
          if (!image.isConnected) {
            state.status = 'cancelled';
            this.imagePrefetches.delete(image);
            return;
          }

          if (state.fullSrcset) image.setAttribute('srcset', state.fullSrcset);
          if (state.fullSizes) image.setAttribute('sizes', state.fullSizes);
          if (state.fullLoading) {
            image.setAttribute('loading', state.fullLoading);
          } else {
            image.removeAttribute('loading');
          }
          if (state.fullFetchPriority) {
            image.setAttribute('fetchpriority', state.fullFetchPriority);
          } else {
            image.removeAttribute('fetchpriority');
          }
          image.setAttribute('src', state.fullSrc);
          delete image.dataset.pdpPlaceholderShown;

          state.status = 'ready';
          this.imagePrefetches.delete(image);
        };

        preloader.onload = () => {
          const decoded = typeof preloader.decode === 'function' ? preloader.decode().catch(() => {}) : Promise.resolve();
          decoded.then(showFullImage);
        };
        preloader.onerror = () => {
          if (state.status === 'pending') {
            state.status = 'error';
          }
        };
        if (state.fullSrcset) preloader.setAttribute('srcset', state.fullSrcset);
        if (state.fullSizes) preloader.setAttribute('sizes', state.fullSizes);
        preloader.src = state.fullSrc;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.classList.contains('pdp-gallery')) this.prefetchMedia(mediaId);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        if (!this.classList.contains('pdp-gallery')) {
          image.onload = () => {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => {
              this.elements.liveRegion.setAttribute('aria-hidden', true);
            }, 2000);
          };
          image.src = image.src;
          return;
        }

        const announceWhenReady = () => {
          if (image.dataset.pdpPlaceholderShown) return;
          image.removeEventListener('load', announceWhenReady);
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.addEventListener('load', announceWhenReady);
        if (image.complete && image.naturalWidth > 0) announceWhenReady();
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
